import React from 'react'
import { useBetSliderStore } from '@stores/betSliderStore'
import { useBuyBonusStore } from '@stores/buyBonusStore'
import { useConfigurationStore } from '@stores/configurationStore'
import { useFormatBetMoney } from '@hooks'
import {
    ViewportType,
    BetOptionItem,
    BetChooserOptionClickedMessage,
    GameUIMessageType,
    BetGroup,
    CollectClickedMessage,
    JackpotEQLevels,
} from 'game-ui-types'
import { useMemo } from 'react'
import { BetButton } from '..'
import { useSpinButtonSectionStore } from '@stores/spinButtonSectionStore'
import { useCountdownFSBonusStore } from '@stores/countdownFSBonusStore'
import { useFreeSpinsBonusesStore } from '@stores/freeSpinsBonusesStore'
import { useLuckySpinsStore } from '@stores/luckySpinsStore'
import { useTranslation } from '@amusnet/translations/react'
import JackpotEQBetSetStatisticsItem from '@components/JackpotSection/JackpotEQ/JackpotEQBetSetStatisticsItem'
import { cn } from '@amusnet/react-ui'

interface BetOptionsProps {
    shouldCloseOnBetChosen: boolean
}

const BetOptions: React.FC<BetOptionsProps> = ({ shouldCloseOnBetChosen }) => {
    const { t } = useTranslation()
    // spin button section store
    const close = useSpinButtonSectionStore((state) => state.close)

    // configuration store
    const viewport = useConfigurationStore((state) => state.viewport)
    const betOptions = useConfigurationStore((state) => state.betOptions)
    const formatMoney = useFormatBetMoney()

    // bet slider store
    const betGroup = useBetSliderStore((state) => state.betGroup)
    const activeIndex = useBetSliderStore((state) => state.activeIndex)
    const updateScrollToIndex = useBetSliderStore((state) => state.updateScrollToIndex)
    const forbiddenBetOptionIndexes = useBetSliderStore((state) => state.forbiddenBetOptionIndexes)
    const setActiveBetGroup = useBetSliderStore((state) => state.setActiveBetGroup)
    const activeBetGroupId = useBetSliderStore((state) => state.activeBetGroupId)
    const setIsScrollingFromBetChooser = useBetSliderStore((state) => state.setIsScrollingFromBetChooser)

    // buy bonus store
    const currentFeatureMultiplier = useBuyBonusStore((state) => state.currentFeatureMultiplier)

    const maxColumns = viewport === ViewportType.MOBILE_PORTRAIT ? 5 : 7

    const layoutClass = betGroup
        ? 'flex flex-col gap-[16px] max-h-[680px] overflow-scroll touch-auto'
        : betOptions.length >= maxColumns
          ? `grid grid-cols-${maxColumns}`
          : 'flex justify-center flex-wrap gap-[12px]'

    const handleButtonClick = (
        betIndex: number,
        groupData?: { groupId: string; bets: BetOptionItem[]; index: number }
    ) => {
        const betChooserOptionClickedMessage: BetChooserOptionClickedMessage = {
            type: GameUIMessageType.BET_CHOOSER_OPTION_CLICKED,
        }

        window.postMessage(betChooserOptionClickedMessage)

        const collectClickedMessage: CollectClickedMessage = {
            type: GameUIMessageType.COLLECT_CLICKED,
        }

        window.postMessage(collectClickedMessage, '*')

        if (shouldCloseOnBetChosen) {
            close()
        }

        if (
            useFreeSpinsBonusesStore.getState().showRemainingSpinsBanner ||
            useLuckySpinsStore.getState().showRemainingSpinsBanner ||
            useCountdownFSBonusStore.getState().showRemainingSpinsBanner
        )
            return

        setIsScrollingFromBetChooser(true)

        if (groupData) {
            setActiveBetGroup(groupData.groupId, groupData.index, groupData.bets)
        } else {
            updateScrollToIndex(betIndex)
        }
    }

    const renderBetButtons = useMemo(() => {
        const renderBet = (
            betValue: number,
            betIndex: number,
            groupData?: { isActive: boolean; index: number; groupId: string; bets: BetOptionItem[] }
        ) => {
            const amount = betValue * currentFeatureMultiplier

            const isForbidden = forbiddenBetOptionIndexes.includes(betIndex)
            const isBetButtonActive = groupData
                ? groupData.isActive && groupData.index === activeIndex
                : betIndex === activeIndex

            return (
                <div key={betIndex} className="m-[5px] touch-inherit">
                    <BetButton
                        label=""
                        amount={formatMoney(amount)}
                        isActive={isBetButtonActive}
                        isForbidden={isForbidden}
                        onClick={() => {
                            if (isForbidden) return

                            handleButtonClick(betIndex, groupData)
                        }}
                        id={`bet-option-${amount}`}
                        className="relative touch-inherit flex flex-col items-center justify-start text-white focus:outline-none px-8"
                    />
                </div>
            )
        }

        const renderJackpotStatistics = (betGroupIndex: number, jackpotEQLevel: JackpotEQLevels) => {
            return (
                <>
                    <JackpotEQBetSetStatisticsItem betGroupIndex={betGroupIndex} jackpotEQLevel={jackpotEQLevel} />
                </>
            )
        }

        const renderBetGroups = (group: BetGroup) => {
            return Object.entries(group).map(([groupId, bets], groupIndex) => {
                const isGroupActive = activeBetGroupId === groupId

                return (
                    <div key={groupId} className="flex flex-col touch-inherit">
                        <div className="inline-flex touch-inherit self-start w-fit items-center px-[10px] py-[4px] rounded-t-[6px] rounded-b-none text-[18px] font-bold leading-[16px] text-white border border-[#3A3A3A] bg-gradient-to-b from-[#5A5A5A] to-[#3E3E3E] font-roboto">
                            <span>{`${t('gameui.betSet')} ${groupIndex + 1}`}</span>
                        </div>

                        <div className="border touch-inherit pt-[6px] pb-[5px] px-[7px] rounded-[8px] rounded-tl-none bg-[#4b4b4b]/40 border-[#646363]">
                            <div className="grid grid-cols-4 gap-[2px]">
                                {renderJackpotStatistics(groupIndex, JackpotEQLevels.BRONZE)}
                                {renderJackpotStatistics(groupIndex, JackpotEQLevels.SILVER)}
                                {renderJackpotStatistics(groupIndex, JackpotEQLevels.GOLD)}
                                {renderJackpotStatistics(groupIndex, JackpotEQLevels.PLATINUM)}
                            </div>

                            <div className="grid touch-inherit grid-cols-5 gap-[2px]">
                                {bets.map((bet, index) =>
                                    renderBet(bet.betAmount, bet.betIndex, {
                                        isActive: isGroupActive,
                                        index,
                                        groupId,
                                        bets,
                                    })
                                )}
                            </div>
                        </div>
                    </div>
                )
            })
        }

        const renderBets = () => (betOptions as number[]).map((betOption, index) => renderBet(betOption, index))

        return <>{betGroup ? renderBetGroups(betGroup) : renderBets()}</>
    }, [
        betOptions,
        betGroup,
        currentFeatureMultiplier,
        activeIndex,
        activeBetGroupId,
        formatMoney,
        forbiddenBetOptionIndexes,
    ])

    return <div className={cn('px-[24px] pb-[24px]', layoutClass)}>{renderBetButtons}</div>
}

export default BetOptions
